import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Bell, BellOff, BellRing, Check } from "lucide-react";
import { isSubscribed, promptSubscribe } from "../lib/oneSignal";

/**
 * Compact "Enable push notifications" pill. Use anywhere — Account page,
 * MyOrders, post-checkout, etc.
 */
export default function EnablePush({ variant = "inline" }) {
    const [supported, setSupported] = useState(true);
    const [optedIn, setOptedIn] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let mounted = true;
        const check = async () => {
            try {
                if (typeof Notification === "undefined") { mounted && setSupported(false); return; }
                const opted = await isSubscribed();
                mounted && setOptedIn(opted);
            } catch { /* swallow */ }
        };
        check();
        const t = setInterval(check, 4000);
        return () => { mounted = false; clearInterval(t); };
    }, []);

    if (!supported) return null;

    const onClick = async () => {
        setBusy(true);
        try {
            const ok = await promptSubscribe();
            setOptedIn(ok);
        } finally { setBusy(false); }
    };

    if (optedIn) {
        return (
            <span className={`inline-flex items-center gap-1 text-xs ${variant === "card" ? "px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-800" : "text-emerald-700"}`} data-testid="push-opted-in">
                <Check className="w-3.5 h-3.5" />Push notifications on
            </span>
        );
    }

    if (variant === "card") {
        return (
            <div className="bg-sky-50 border border-sky-200 p-4 flex items-start gap-3" data-testid="push-card">
                <BellRing className="w-5 h-5 text-sky-700 mt-0.5" />
                <div className="flex-1">
                    <div className="font-medium text-stone-900 text-sm">Get instant updates</div>
                    <div className="text-xs text-stone-600 mt-1">Order shipped, refill due, appointment reminders — straight to your browser/phone.</div>
                </div>
                <Button onClick={onClick} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="push-enable-btn">
                    {busy ? "…" : "Enable"}
                </Button>
            </div>
        );
    }
    return (
        <Button onClick={onClick} disabled={busy} variant="outline" size="sm" className="rounded-none border-sky-300 text-sky-700" data-testid="push-enable-btn">
            <Bell className="w-3.5 h-3.5 mr-1" />{busy ? "…" : "Enable notifications"}
        </Button>
    );
}
